/**
 * The operator words for every donations reconciliation issue code — a MIRROR of
 * VeevartBackend's `RECONCILIATION_ISSUE_MESSAGES`
 * (`domains/fundraising/migration/src/donations/reconciliation/reconciliation-issues.ts`),
 * copied as the backend wrote them. The report carries codes only (contract
 * version 2), so the screen maps them here, as the backend's
 * `reconciliationMessageOf` does: an unknown code shows as itself, never as a
 * guess. A new code upstream lands here in the Backoffice change that follows it.
 */

const PREFIX = 'FUNDRAISING_DONATIONS_RECONCILIATION_';

export const RECONCILIATION_ISSUE_CODES = Object.freeze({
    STAMP_FAILED: `${PREFIX}STAMP_FAILED`,
    PAYMENT_LINKED_DONATION_NOT: `${PREFIX}PAYMENT_LINKED_DONATION_NOT`,
    DONATION_LINKED_PAYMENTS_NOT: `${PREFIX}DONATION_LINKED_PAYMENTS_NOT`,
    WON_REFUNDED_IN_FULL: `${PREFIX}WON_REFUNDED_IN_FULL`,
    RECEIVED_AND_WRITTEN_OFF_ABOVE_AMOUNT: `${PREFIX}RECEIVED_AND_WRITTEN_OFF_ABOVE_AMOUNT`,
    V2_BORN_NOT_IN_VEEVART: `${PREFIX}V2_BORN_NOT_IN_VEEVART`,
    DONOR_NOT_IN_VEEVART: `${PREFIX}DONOR_NOT_IN_VEEVART`,
    ORGANIZATION_NOT_IN_VEEVART: `${PREFIX}ORGANIZATION_NOT_IN_VEEVART`,
    AMOUNT_DIFFERS: `${PREFIX}AMOUNT_DIFFERS`,
    FUND_SPLIT_DIFFERS: `${PREFIX}FUND_SPLIT_DIFFERS`,
    COMMITMENT_NOT_IN_VEEVART: `${PREFIX}COMMITMENT_NOT_IN_VEEVART`,
    MIGRATED_NOT_IN_VEEVART: `${PREFIX}MIGRATED_NOT_IN_VEEVART`,
    NOT_READ_FROM_SALESFORCE: `${PREFIX}NOT_READ_FROM_SALESFORCE`,
    PENDING_BALANCE_DIFFERS: `${PREFIX}PENDING_BALANCE_DIFFERS`,
    TOTALS_DIFFER: `${PREFIX}TOTALS_DIFFER`,
    CUSTOM_FISCAL_YEARS_UNREADABLE: `${PREFIX}CUSTOM_FISCAL_YEARS_UNREADABLE`,
    TOTALS_CURRENCY_NOT_COMPARED: `${PREFIX}TOTALS_CURRENCY_NOT_COMPARED`,
    CONSTITUENT_DIFFERS_FROM_SALESFORCE: `${PREFIX}CONSTITUENT_DIFFERS_FROM_SALESFORCE`,
    HELD_TWICE: `${PREFIX}HELD_TWICE`,
});

const C = RECONCILIATION_ISSUE_CODES;

export const RECONCILIATION_ISSUE_MESSAGES: Readonly<Record<string, string>> = Object.freeze({
    [C.STAMP_FAILED]:
        'Copied to Veevart, but Salesforce does not hold its link, so Salesforce may still collect ' +
        'it. Sync again to write the missing link.',
    [C.PAYMENT_LINKED_DONATION_NOT]:
        'Some of its payments are linked in Salesforce, but the donation itself is not. Sync again ' +
        'to write the donation link.',
    [C.DONATION_LINKED_PAYMENTS_NOT]:
        'The donation is linked in Salesforce, but some payments copied to Veevart are not. Sync ' +
        'again to write their links.',
    [C.WON_REFUNDED_IN_FULL]:
        'Won in Salesforce but refunded in full. Salesforce turns it into a refunded, lost donation ' +
        'when it is linked, and Veevart copies it that way. Check it in Salesforce.',
    [C.RECEIVED_AND_WRITTEN_OFF_ABOVE_AMOUNT]:
        'Its received and written-off payments add up to more than the donation amount in ' +
        'Salesforce. Correct the payments in Salesforce.',
    [C.V2_BORN_NOT_IN_VEEVART]:
        'Salesforce links it to a donation created in Veevart, but Veevart does not hold that ' +
        'donation. Nothing was created. Do not recreate it by hand: escalate it to Backend.',
    [C.DONOR_NOT_IN_VEEVART]:
        'Its donor is linked to a contact Veevart does not hold. Sync Contacts again, then run the ' +
        'reconciliation again.',
    [C.ORGANIZATION_NOT_IN_VEEVART]:
        'Its organization is linked to an organization Veevart does not hold. Sync Organizations ' +
        'again, then run the reconciliation again.',
    [C.AMOUNT_DIFFERS]:
        'Its amount, received or refunded money differs between Salesforce and Veevart. Veevart ' +
        'keeps its own value: correct it in Veevart if Salesforce is right.',
    [C.FUND_SPLIT_DIFFERS]:
        'Its fund split differs between Salesforce and Veevart. Veevart keeps its own split: ' +
        'correct it in Veevart if Salesforce is right.',
    [C.COMMITMENT_NOT_IN_VEEVART]:
        'Veevart holds this donation only in part: its balance and payments are missing. Do not ' +
        'edit it by hand: escalate it to Backend.',
    [C.MIGRATED_NOT_IN_VEEVART]:
        'Marked as copied, but Veevart no longer holds it. Find out why it was removed in Veevart ' +
        'before you sync again: a sync copies it back.',
    [C.NOT_READ_FROM_SALESFORCE]:
        'Veevart holds a copy, but Salesforce no longer lists it as a closed donation, so it ' +
        'cannot be compared. Check it in Salesforce.',
    [C.PENDING_BALANCE_DIFFERS]:
        'The pending-balance warning of its last copy does not match the balance Veevart shows ' +
        'now. Sync again to refresh the warning.',
    [C.TOTALS_DIFFER]:
        'The totals of this fiscal year and stage differ between Salesforce and Veevart. Review ' +
        'the donations listed in this report; nothing was changed.',
    [C.CUSTOM_FISCAL_YEARS_UNREADABLE]:
        'The custom fiscal years of Salesforce could not be read, so Veevart totals use the ' +
        'standard fiscal year. Totals of a custom year may differ.',
    [C.CONSTITUENT_DIFFERS_FROM_SALESFORCE]:
        'The donor or organization Veevart holds for it is not the one Salesforce names. Veevart ' +
        'keeps its own: correct it in Veevart if Salesforce is right.',
    [C.HELD_TWICE]:
        'Veevart holds this donation twice: as the copied donation and inside an installment ' +
        'setup. Do not edit it by hand: escalate it to Backend.',
    [C.TOTALS_CURRENCY_NOT_COMPARED]:
        'These donations use more than one currency, so the totals compare counts only. Every ' +
        'donation amount is still compared exactly.',
});

/** The operator words for an issue code; the code itself when this build does not know it. */
export function reconciliationMessageOf(code: string): string {
    return RECONCILIATION_ISSUE_MESSAGES[code] ?? code;
}
